/**
 * 账户管理工具类
 * 用于管理多账户登录和切换功能
 */

import storage from './storage';

class AccountManager {
  /**
   * 获取所有账户信息
   * @returns {Array} 账户列表
   */
  getAllAccounts() {
    try {
      const accountsStr = storage.getItem('allAccounts');
      if (accountsStr) {
        return JSON.parse(accountsStr);
      }
      return [];
    } catch (error) {
      console.error('获取账户列表失败:', error);
      return [];
    }
  }

  /**
   * 检查是否有多个账户
   * @returns {Boolean}
   */
  hasMultipleAccounts() {
    return storage.getItem('hasMultipleAccounts') === 'true';
  }

  /**
   * 获取当前账户索引
   * @returns {Number}
   */
  getCurrentAccountIndex() {
    const index = storage.getItem('currentAccountIndex');
    return index !== null ? parseInt(index, 10) : 0;
  }

  /**
   * 获取当前账户信息
   * @returns {Object|null}
   */
  getCurrentAccount() {
    const accounts = this.getAllAccounts();
    const index = this.getCurrentAccountIndex();
    return accounts[index] || null;
  }

  /**
   * 切换到指定账户
   * @param {Number} accountIndex - 账户索引
   * @returns {Object|null} 账户信息
   */
  switchAccount(accountIndex) {
    const accounts = this.getAllAccounts();
    if (accountIndex >= 0 && accountIndex < accounts.length) {
      const account = accounts[accountIndex];

      // 更新当前账户索引
      storage.setItem('currentAccountIndex', accountIndex.toString());

      // 更新 token 和其他信息
      storage.setItem('token', account.token);
      storage.setItem('companyName', account.name);

      return account;
    }
    return null;
  }

  /**
   * 获取账户数量
   * @returns {Number}
   */
  getAccountCount() {
    return this.getAllAccounts().length;
  }

  /**
   * 清除所有账户信息
   */
  clearAllAccounts() {
    storage.clearItem('allAccounts');
    storage.clearItem('hasMultipleAccounts');
    storage.clearItem('currentAccountIndex');
  }

  /**
   * 保存所有账户信息
   * @param {Array} accounts - 账户列表
   */
  saveAllAccounts(accounts) {
    if (Array.isArray(accounts) && accounts.length > 0) {
      storage.setItem('allAccounts', JSON.stringify(accounts));
      storage.setItem(
        'hasMultipleAccounts',
        accounts.length > 1 ? 'true' : 'false',
      );

      // 如果没有设置当前账户索引，默认设置为0
      if (storage.getItem('currentAccountIndex') === null) {
        storage.setItem('currentAccountIndex', '0');
      }
    }
  }

  /**
   * 获取账户显示信息列表（用于UI展示）
   * @returns {Array} 包含 name 和 userName 的数组
   */
  getAccountDisplayList() {
    const accounts = this.getAllAccounts();
    return accounts.map((account, index) => ({
      index,
      name: account.name,
      userName: account.userName,
      isCurrent: index === this.getCurrentAccountIndex(),
    }));
  }
}

// 导出单例
export default new AccountManager();
